import React, { useEffect } from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'

import { B2BQr, C2BQr } from '../../../types.ts'
import { useMutateDeleteC2B } from '../../../services'
import { useDialog } from '../../../hooks'
import { Amount, Dialog, InfoBox, useSnackbarContext } from '../../../components'
import { useQueryClient } from '@tanstack/react-query'

export const DeleteStaticQr = ({
  type,
  qrcType,
  requestId,
  reference,
  amount,
  paymentPurpose,
}: C2BQr | B2BQr): React.ReactElement | null => {
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()
  const { addSnack } = useSnackbarContext()
  const qc = useQueryClient()

  const { mutate, isPending, status, data, reset } = useMutateDeleteC2B(requestId)
  const isC2B = type === 'C2B'
  const isB2B = type === 'B2B'
  const isStatic = qrcType === '01'

  const canDelete = (isC2B && isStatic) || isB2B

  useEffect(() => {
    switch (status) {
      case 'success': {
        const response = data?.data
        // при удалении возвращается 204, при повторном удалении 200 со state: REJECTED
        const isSuccess = data?.status === 204
        const errorMessage =
          response.state === 'REJECTED' && 'errorMessage' in response
            ? response?.errorMessage ?? 'Произошла ошибка при удалении QR, попробуйте ещё раз'
            : ''

        addSnack({
          message: isSuccess ? 'QR удалён' : errorMessage,
          severity: isSuccess ? 'success' : 'error',
          timeout: isSuccess ? 6000 : 3000,
          closeIcon: false,
        })

        if (isSuccess) {
          qc.invalidateQueries({ queryKey: ['C2B_list'] })
          handleHideDialog()
          reset()
        }

        break
      }

      case 'error': {
        addSnack({
          message: 'Произошла непредвиденная ошибка, попробуйте ещё раз',
          severity: 'error',
          timeout: 3000,
          closeIcon: false,
        })
        break
      }

      default:
        break
    }
  }, [status])

  // if (!isC2B) return null

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Button size="medium" color="secondary" disabled={!canDelete} onClick={handleShowDialog}>
        Удалить
      </Button>
      {showDialog && (
        <Dialog
          title="Подтвердите удаление"
          open
          maxWidth="md"
          onClose={handleHideDialog}
          actions={
            <>
              <Button fullWidth color="secondary" size="large" onClick={handleHideDialog}>
                Отмена
              </Button>
              <Button fullWidth color="primary" size="large" onClick={() => mutate()} disabled={isPending}>
                {isPending ? <CircularProgress color="inherit" size={16} /> : 'Удалить QR'}
              </Button>
            </>
          }
          content={
            <>
              <Typography sx={{ mb: 2 }}>Вы уверены что хотите удалить данный шаблон?</Typography>
              <InfoBox>
                <Stack rowGap={0.5}>
                  <Typography>
                    <b>№ заказаа в магазине</b>: {reference}
                  </Typography>
                  <Typography>
                    <b>Назначение платежа</b>: {paymentPurpose}
                  </Typography>
                  <Stack direction="row" gap={0.5}>
                    <Typography>
                      <b>Сумма</b>:
                    </Typography>
                    <Amount value={amount} />
                  </Stack>
                </Stack>
              </InfoBox>
            </>
          }
        />
      )}
    </Box>
  )
}
